import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Matches the platform shell content wrapper `p-6` bottom padding. */
const PAGE_BOTTOM_GAP_PX = 24;

/**
 * Measures the element's top edge and fills down to the viewport bottom,
 * minus page bottom padding. Recalculates on mount and window resize.
 *
 * Do not ResizeObserver documentElement: applying height can toggle scrollbars and
 * retrigger observe → measure → setHeight in a loop.
 */
export const useFillAvailableHeight = (enabled: boolean) => {
    const ref = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number>();

    const measure = useCallback(() => {
        if (!enabled || !ref.current) return;
        const next = Math.max(0, Math.floor(window.innerHeight - ref.current.getBoundingClientRect().top - PAGE_BOTTOM_GAP_PX));
        setHeight((prev) => {
            if (prev !== undefined && Math.abs(prev - next) <= 1) return prev;
            return next;
        });
    }, [enabled]);

    useLayoutEffect(() => {
        measure();
    }, [measure]);

    useEffect(() => {
        if (!enabled) return;
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [enabled, measure]);

    return { ref, height };
};
